import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import { Error, Hint, Label } from '@progress/kendo-react-labels';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Autocomplete from 'react-google-autocomplete';
import { usePlacesWidget } from 'react-google-autocomplete';

import { selectAppSettings } from '../../../../features';
import { geocodingGoogleService } from '../../../../services';

export const GoogleAddressAutoCompleteInput = (fieldRenderProps: FieldRenderProps) => {
  const autocompleteRef = useRef<HTMLInputElement>(null);

  const appSettings = useSelector(selectAppSettings);
  const [val, setVal] = useState(() => fieldRenderProps.value);

  const { validationMessage, touched, label, id, valid, disabled, hint, type, optional, required, ...others } = fieldRenderProps;

  const showValidationMessage: string | boolean | null = touched && validationMessage;
  const showHint: boolean = !showValidationMessage && hint;
  const hintId: string = showHint ? `${id}_hint` : '';
  const errorId: string = showValidationMessage ? `${id}_error` : '';
  const isInvalid = touched && !valid;

  const onChange = useCallback(
    (e: { target?: any; value?: any }) => {
      const val = e.target.value || '';
      fieldRenderProps.onChange({ target: e.target, value: val });
      setVal(val);
    },
    [fieldRenderProps],
  );

  const onPlaceSelected = useCallback(
    (place: unknown, ref: any, autocompleteRef: any) => {
      const parsedAddress = geocodingGoogleService.parseAddress(place);
      if (parsedAddress && parsedAddress.lng && parsedAddress.lat && fieldRenderProps?.onPlaceSelected) {
        fieldRenderProps.onPlaceSelected({
          position: [parsedAddress.lng, parsedAddress.lat],
          address: parsedAddress,
        });
      }

      fieldRenderProps.onChange({ target: ref, value: ref?.value });
      setVal(ref?.value);
    },
    [fieldRenderProps],
  );

  const { ref } = usePlacesWidget({
    apiKey: appSettings?.map.google_api_token,
    options: {
      types: ['address'],
      fields: ['place_id', 'address_component', 'geometry'],
    },

    onPlaceSelected: (place) => {
      fieldRenderProps?.onPlaceSelected && fieldRenderProps.onPlaceSelected(place);
    },
  });

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} editorDisabled={disabled} optional={optional} className={`${required ? 'required' : null}`}>
        {label}
      </Label>
      <div className={'k-form-field-wrap'}>
        <Autocomplete
          ref={autocompleteRef}
          apiKey={appSettings?.map.google_api_token}
          options={{
            types: ['address'],
            fields: ['place_id', 'address_component', 'geometry'],
          }}
          language='en'
          onPlaceSelected={onPlaceSelected}
          defaultValue={val}
          inputAutocompleteValue={val}
          onChange={onChange}
          aria-describedby={`${hintId} ${errorId}`}
          className={`k-input k-input-md k-rounded-md k-input-solid ${isInvalid ? 'k-invalid' : ''}`}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  );
};
